import * as React from "react"
import { Search, X } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  SearchField — the toolbar search used on list pages (Projects, Documents, Inventory, Shop Drawings,
  Procurement, Invoicing …).

    <SearchField value={q} onChange={setQ} placeholder="Search name, client…" label="Search projects" testId="search-projects" />

  - "/" focuses it from anywhere on the page (`shortcut`, default on) — not while typing in another field,
    and not while a dialog / menu is open. `onlyWhenVisible` skips the shortcut while the field is hidden
    (e.g. on an inactive tab that stays mounted).
  - Escape clears it; the × button clears it; a "/" hint shows while empty (not on phones).
  Props: value, onChange(text), placeholder, label (screen-reader name), className, inputClassName, testId,
  shortcut, onlyWhenVisible, onKeyDown. The ref goes to the <input>.
*/
const SearchField = React.forwardRef(function SearchField({
  value,
  onChange,
  placeholder = "Search…",
  label = "Search",
  className,
  inputClassName,
  testId,
  shortcut = true,
  onlyWhenVisible = false,
  onKeyDown,
}, forwardedRef) {
  const localRef = React.useRef(null)
  const ref = forwardedRef || localRef

  React.useEffect(() => {
    if (!shortcut) return undefined
    const onKey = (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target
      const tag = target?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return
      if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]')) return
      if (!ref.current) return
      if (onlyWhenVisible && !ref.current.offsetParent) return
      event.preventDefault()
      ref.current.focus()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [ref, shortcut, onlyWhenVisible])

  return (
    <label className={cn("flex h-9 min-w-0 items-center gap-2 rounded-control border border-line bg-surface px-3 text-ink-4 focus-within:border-[color:var(--brand-ink)] focus-within:ring-2 focus-within:ring-[color:var(--brand-soft-border)]", className)}>
      <Search className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      <span className="sr-only">{label}</span>
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value) {
            event.preventDefault()
            onChange("")
          }
          onKeyDown?.(event)
        }}
        placeholder={placeholder}
        data-testid={testId}
        className={cn("h-full min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-4 [&::-webkit-search-cancel-button]:hidden", inputClassName)}
      />
      {value ? (
        <button type="button" onClick={() => onChange("")} aria-label="Clear search" className="grid h-5 w-5 shrink-0 place-items-center rounded text-ink-3 hover:bg-surface-hover hover:text-ink">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : shortcut ? (
        <kbd className="ui-kbd shrink-0 max-sm:hidden" aria-hidden="true">/</kbd>
      ) : null}
    </label>
  )
})

export { SearchField }
